<template>
  <v-card color="background" elevation=0>
    <v-row align="center" class="text-center detection-categories-box">
      <v-col class="d-flex justify-start align-center">
        <v-icon
          :color="categories[detection].color"
        > mdi-square-rounded
        </v-icon>
        <span class="mk-text--h2 ml-2">{{ categories[detection].text }}</span>
      </v-col>
      <v-spacer></v-spacer>
      <v-col class="d-flex justify-end">
        <v-icon
          v-for="category in categories.slice(detection + 1).concat(categories.slice(0, detection))"
          :key="category"
          :color="category.color"
          @click="detection = categories.indexOf(category)"
        > mdi-square-rounded
        </v-icon>
      </v-col>
    </v-row>
    <v-row align="center">
      <v-carousel 
        hide-delimiters
        v-model="detection"
        height="272px"
      >
        <v-carousel-item
          v-for="category in categories"
          :key="category.name"
          :src="`${$imageSrc}er6_assets/${category.image}`"
        />
      </v-carousel>
    </v-row>
    <v-row>
      <v-col cols=12 class="text-center">
        <div class="mk-text--grey60">explorer.exe executes kryptonite_gift.exe</div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols=12>
        <span>Definition</span>
        <div v-html="categories[detection].description"></div>
      </v-col>
    </v-row>
  </v-card>
</template>



<script setup>
  import { ref } from 'vue';

  const detection = ref(0);

  const none = `Execution of the sub step was successful; however, evidence provided by the vendor does
  not meet the documented Detection Criteria, or there was no evidence of Red Team activity provided.  There are no
  modifiers, notes, or screenshots included with a None.`

  const general = `<p class="m-spacing--top-3">The solution autonomously identified that malicious/suspicious
  event(s) occurred and met the Detection Criteria. However, the collected evidence did not provide details as
  to <b>why</b> the action was performed (Tactic) or <b>how</b> the action was performed (Technique) through the
  ATT&CK Tactic/Technique or equivalent level of enrichment.</p>
  <p class="m-spacing--top-5">General Detections must include the following:</p>
  <p class="m-spacing--top-3">
    <ul class="ml-4">
      <li class="m-spacing--top-3">What - What happened? - what was the error/alert code</li>
      <li class="m-spacing--top-3">Where - Where did this occur? Hostname, IP, domain, geo, building, subnet, etc</li>
      <li class="m-spacing--top-3">When - When did it happen? Timestamp(s) relevant to alerts</li>
      <li class="m-spacing--top-3">Who - Who performed the action (user)?</li>
    </ul>
  </p>`

  const tacticIncomplete = `<p class="m-spacing--top-3">The solution autonomously identified that
  malicious/suspicious event(s) occurred and met the Detection Criteria.  However, the collected evidence did not
  correctly identify <b>why</b> the action was performed (Tactic) or provide details as to <b>how</b> the action
  was performed (Technique) through the ATT&CK Tactic/Technique or equivalent level of enrichment.</p>
  <p class="m-spacing--top-5">General Detections must include the following:</p>
  <p class="m-spacing--top-3">
    <ul class="ml-4">
      <li class="m-spacing--top-3">What - What happened? - what was the error/alert code</li>
      <li class="m-spacing--top-3">Where - Where did this occur? Hostname, IP, domain, geo, building, subnet, etc</li>
      <li class="m-spacing--top-3">When - When did it happen? Timestamp(s) relevant to alerts</li>
      <li class="m-spacing--top-3">Who - Who performed the action (user)?</li>
    </ul>
  </p>`

  const tactic = `<p class="m-spacing--top-3">The solution autonomously identified that malicious/suspicious
  event(s) occurred, met the Detection Criteria for General, and provided details as to <b>why</b> the action was
  performed (Tactic).  However, the collected evidence did not provide details as to <b>how</b> the action was
  performed (Technique) through the ATT&CK Technique or equivalent level of enrichment.</p>
  <p class="m-spacing--top-5">Tactic Detections must include the following:</p>
  <p class="m-spacing--top-3">
    <ul class="ml-4">
      <li class="m-spacing--top-3">What - What happened? - what was the error/alert code</li>
      <li class="m-spacing--top-3">Where - Where did this occur? Hostname, IP, domain, geo, building, subnet, etc</li>
      <li class="m-spacing--top-3">When - When did it happen? Timestamp(s) relevant to alerts</li>
      <li class="m-spacing--top-3">Who - Who performed the action (user)?</li>
      <li class="m-spacing--top-3">Why - Why was the action performed?</li>
    </ul>
  </p>`

  const techniqueIncomplete = `<p class="m-spacing--top-3">The solution autonomously identified that
  malicious/suspicious event(s) occurred, met the Detection Criteria for General, and provided details as to
  <b>why</b> the action was performed (Tactic). However, the collected evidence did not correctly identify
  <b>how</b> the action was performed (Technique) through the ATT&CK Technique or equivalent level of enrichment.</p>
  <p class="m-spacing--top-5">Tactic Detections must include the following:</p>
  <p class="m-spacing--top-3">
    <ul class="ml-4">
      <li class="m-spacing--top-3">What - What happened? - what was the error/alert code</li>
      <li class="m-spacing--top-3">Where - Where did this occur? Hostname, IP, domain, geo, building, subnet, etc</li>
      <li class="m-spacing--top-3">When - When did it happen? Timestamp(s) relevant to alerts</li>
      <li class="m-spacing--top-3">Who - Who performed the action (user)?</li>
      <li class="m-spacing--top-3">Why - Why was the action performed?</li>
    </ul>
  </p>`

  const technique = `<p class="m-spacing--top-3">The solution autonomously identified that malicious/suspicious
  event(s) occurred and met the Detection Criteria for Tactic and provided details as to <b>how</b> the action was
  performed (Technique).</p>
  <p class="m-spacing--top-5">Technique Detections must include the following:</p>
  <p class="m-spacing--top-3">
    <ul class="ml-4">
      <li class="m-spacing--top-3">What - What happened? - what was the error/alert code</li>
      <li class="m-spacing--top-3">Where - Where did this occur? Hostname, IP, domain, geo, building, subnet, etc</li>
      <li class="m-spacing--top-3">When - When did it happen? Timestamp(s) relevant to alerts</li>
      <li class="m-spacing--top-3">Who - Who performed the action (user)?</li>
      <li class="m-spacing--top-3">Why - Why was the action performed?</li>
      <li class="m-spacing--top-3">How - How was the action performed?</li>
    </ul>
  </p>`

  const categories = [
    {
      image: 'technique_example.webp',
      description: technique,
      text: 'Technique',
      color: '#403386'
    },
    {
      image: 'technique_incomplete_example.webp',
      description: techniqueIncomplete,
      text: 'Tactic',
      color: '#89007D'
    },
    {
      image: 'tactic_example.webp',
      description: tactic,
      text: 'Tactic',
      color: '#89007D'
    },
    {
      image: 'tactic_incomplete_example.webp',
      description: tacticIncomplete,
      text: 'General',
      color: '#FB7139'
    },
    {
      image: 'general_example.webp',
      description: general,
      text: 'General',
      color: '#FB7139'
    },
    {
      image: 'general_incomplete_example.webp',
      description: none,
      text: 'None',
      color: '#F7AC43'
    },
    {
      image: 'none_example.webp',
      description: none,
      text: 'None',
      color: '#F7AC43'
    },
  ]
</script>

<style lang="scss">
  @import "../../sass/pages/_detection-category-carousel.scss";
</style>